import { useLayoutEffect, type RefObject } from 'react';
import {
  clearMobileNavTitleStages,
  computeMobileNavTitleStages,
  writeMobileNavTitleStages,
} from '../components/layout/mobileNavTitleStages';

/**
 * Absolute scroll-range stages for the mobile large→compact title transition.
 *
 * Geometry is measured each frame; each stage var is a clipped remap so
 * scrubbing between stages always matches the current scroll position.
 */
export function useMobileLargeTitleProgress(
  enabled: boolean,
  titleRef: RefObject<HTMLElement | null>,
) {
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (!enabled) {
      clearMobileNavTitleStages(root);
      return;
    }

    let raf = 0;

    const measure = () => {
      raf = 0;
      const titleEl = titleRef.current;
      if (!titleEl) {
        writeMobileNavTitleStages(root, { tuck: 0, compact: 0, material: 0 });
        return;
      }

      const header = document.querySelector<HTMLElement>('[data-app-header]');
      const headerBottom = header?.getBoundingClientRect().bottom ?? 60;
      const titleRect = titleEl.getBoundingClientRect();
      writeMobileNavTitleStages(
        root,
        computeMobileNavTitleStages({
          headerBottom,
          titleTop: titleRect.top,
          titleHeight: titleRect.height,
        }),
      );
    };

    const onScrollOrResize = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };

    writeMobileNavTitleStages(root, { tuck: 0, compact: 0, material: 0 });
    measure();
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
      if (raf) cancelAnimationFrame(raf);
      clearMobileNavTitleStages(root);
    };
  }, [enabled, titleRef]);
}
